import { MODELS_TIER_DISPLAY_NAMES } from "@app/components/model_picker/modelPickerUtils";
import {
  seatTypeChipColor,
  seatTypeDisplayName,
} from "@app/components/workspace/billing/seatTypeUtils";
import {
  buildMemberNameColumn,
  MemberNameSkeleton,
} from "@app/components/workspace/member_name_column";
import { ModelTiersInfoButton } from "@app/components/workspace/ModelTiersInfoModal";
import {
  AT_POOL_LIMIT_BAR_CLASSES,
  getSeatBarClasses,
  MUTED_BAR_CLASSES,
  OVER_POOL_LIMIT_BAR_CLASSES,
  OVERAGE_BAR_CLASSES,
} from "@app/components/workspace/seat_styles";
import { formatConsumptionDate } from "@app/lib/analytics/consumption_period";
import type { PremiumModelMessageUsage } from "@app/lib/api/assistant/rate_limits";
import type {
  MemberFairUseUsage,
  MemberUsageType,
} from "@app/lib/api/credits/members_usage";
import {
  computePoolLimitAwuCredits,
  computeSeatUsage,
} from "@app/lib/api/credits/seat_usage";
import { formatCredits, roundCredits } from "@app/lib/client/credits";
import type { UserModelTierSelection } from "@app/lib/client/model_tier_options";
import {
  getUserModelTierMenuItemsWithSelection,
  INHERIT_MODEL_TIER,
  toUserModelTierSelection,
} from "@app/lib/client/model_tier_options";
import {
  formatUserModelTierInheritLabel,
  resolveModelTiersForUser,
} from "@app/lib/client/model_tiers";
import { getActiveLocale } from "@app/lib/i18n/active_locale";
import { formatDate } from "@app/lib/i18n/format";
import type { ModelsTierDefinition } from "@app/lib/model_tiers/allowed_tiers";
import { getMaxTierName } from "@app/lib/model_tiers/tier_order";
import type { EffectiveSpendLimitSource } from "@app/lib/spend_limits/effective";
import type { CreditUsageTarget } from "@app/types/api/credits/usage_status";
import type { ModelsTierName } from "@app/types/assistant/models/model_tiers";
import type { MembershipSeatType } from "@app/types/memberships";
import {
  isMembershipSeatType,
  isPaidSeatType,
  SEAT_TYPE_ORDER,
  toBaseSeatType,
} from "@app/types/memberships";
import { assertNeverAndIgnore } from "@app/types/shared/utils/assert_never";
import type { DataTableSkeletonCellProps, MenuItem } from "@dust-tt/sparkle";
import {
  AlertCircle,
  Button,
  Chip,
  Clock,
  CoinsStacked03,
  createSelectionColumn,
  DataTable,
  DataTableSkeleton,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  LoadingBlock,
  ProgressBar,
  Spinner,
  Tooltip,
} from "@dust-tt/sparkle";
import type { MessageDescriptor } from "@lingui/core";
import { msg, plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import type {
  CellContext,
  ColumnDef,
  PaginationState,
  RowSelectionState,
  SortingState,
} from "@tanstack/react-table";
import { useMemo, useRef } from "react";

const EMPTY_USER_MODEL_TIER_SELECTION_BY_USER_ID: Record<
  string,
  UserModelTierSelection
> = {};
const EMPTY_USER_ALLOWED_MODEL_TIERS_BY_USER_ID: Record<
  string,
  ModelsTierName[]
> = {};
const EMPTY_GROUP_MODEL_TIERS_BY_GROUP_ID: Record<string, ModelsTierName[]> =
  {};
const EMPTY_WORKSPACE_ALLOWED_MODEL_TIERS: ModelsTierName[] = [];
const EMPTY_GROUP_NAME_TO_ID = new Map<string, string>();
const EMPTY_MODEL_TIER_DEFINITION_BY_NAME = new Map<
  ModelsTierName,
  ModelsTierDefinition
>();
const NOOP_ON_MEMBER = (_member: MemberUsageType) => {};
const ALWAYS_CAN_UPGRADE_SEAT = (_member: MemberUsageType) => true;
const ALWAYS_CAN_EDIT_SPEND_LIMIT = (_member: MemberUsageType) => true;

const DEFAULT_PREMIUM_MESSAGE_WINDOW_DAYS = 7;

type RowData = {
  sId: string;
  name: string;
  email: string | null;
  image: string | null;
  groups: string[];
  seatType: MembershipSeatType | null;
  memberUsageLimit: number | null;
  seatBalanceAwu: number | null;
  consumedAwuCredits: number;
  consumedFromAllowanceAwuCredits: number;
  consumedFromPoolAwuCredits: number;
  spendLimitAwuCredits: number | null;
  spendLimitSource: EffectiveSpendLimitSource;
  spendLimitGroupName: string | null;
  poolCapOverrideExpiresAt: string | null;
  poolCapOverridePreviousAwuCredits: number | null;
  scheduledSeatType: MembershipSeatType | null;
  scheduledSeatChangeAt: string | null;
  isTotalAllowedUsagePending: boolean;
  isSeatChangePending: boolean;
  overallUsageTarget: CreditUsageTarget | null;
  isSpendCapped: boolean;
  canUpgradeSeat: boolean;
  onOpenChangeSeatRecap: () => void;
  onOpenSpendLimitRecap: () => void;
  premiumMessageUsage: PremiumModelMessageUsage | null;
  fairUse: MemberFairUseUsage | null;
  modelTiersSummary: string;
  hasUserLevelModelTiersOverride: boolean;
  menuItems: MenuItem[];
};

type Info = CellContext<RowData, string>;

type Translate = (descriptor: MessageDescriptor) => string;

function getSeatName(seatType: MembershipSeatType, t: Translate): string {
  return seatType === "none"
    ? t(msg`No seat`)
    : seatTypeDisplayName(seatType, t);
}

function MemberUsageSkeletonCell({
  columnId,
  rowIndex,
}: DataTableSkeletonCellProps) {
  switch (columnId) {
    case "select":
      return <LoadingBlock className="h-4 w-4 rounded" />;
    case "name":
      return <MemberNameSkeleton rowIndex={rowIndex} />;
    case "groups":
    case "modelTiers":
      return <LoadingBlock className="h-3 w-28 max-w-full" />;
    case "seatType":
      return (
        <div className="flex items-center gap-1.5">
          <LoadingBlock className="h-5 w-5" />
          <LoadingBlock className="h-3 w-12" />
        </div>
      );
    case "seatsIcon":
      return <LoadingBlock className="mx-auto h-5 w-8 rounded-lg" />;
    case "overallUsageTarget":
      return <LoadingBlock className="mx-auto h-5 w-5" />;
    case "seatUsage":
      return <LoadingBlock className="mx-auto h-3 w-8" />;
    case "consumedFromPoolAwuCredits":
    case "premiumMessageUsage":
      return (
        <div className="flex flex-col gap-1 pr-3">
          <div className="flex h-4 items-center justify-between">
            <LoadingBlock className="h-3 w-10" />
            <LoadingBlock className="h-3 w-10" />
          </div>
          <div className="flex h-3 items-center">
            <LoadingBlock className="h-1 w-full rounded-full" />
          </div>
        </div>
      );
    case "fairUse":
      return (
        <div className="flex flex-col gap-1">
          <div className="flex h-4 items-center justify-between">
            <LoadingBlock className="h-3 w-10" />
            <LoadingBlock className="h-3 w-10" />
          </div>
          <LoadingBlock className="h-3 w-full rounded-full" />
        </div>
      );
    case "actions":
      return <LoadingBlock className="h-8 w-8 rounded-xl" />;
    default:
      return null;
  }
}

// Builds the tooltip explaining a scheduled seat change, e.g.
// "This user will be downgraded to Free at the end of the billing period (July 1)".
function getScheduledSeatChangeLabel(
  currentSeatType: MembershipSeatType | null,
  scheduledSeatType: MembershipSeatType,
  scheduledSeatChangeAt: string | null,
  t: Translate
): string {
  const changeDate = scheduledSeatChangeAt
    ? formatDate(
        new Date(scheduledSeatChangeAt),
        {
          month: "long",
          day: "numeric",
          timeZone: "UTC",
        },
        getActiveLocale()
      )
    : null;

  // A same-tier monthly→yearly commitment (e.g. pro -> pro_yearly) isn't a
  // tier upgrade/downgrade/change — the user stays on the same tier, only the
  // billing cadence switches. Call that out explicitly instead of the
  // confusing "changed to Pro" wording, since the user is already on Pro.
  const isMonthlyToYearlySwitch =
    isMembershipSeatType(currentSeatType) &&
    isPaidSeatType(currentSeatType) &&
    !currentSeatType.endsWith("_yearly") &&
    scheduledSeatType.endsWith("_yearly") &&
    toBaseSeatType(currentSeatType) === toBaseSeatType(scheduledSeatType);
  if (isMonthlyToYearlySwitch) {
    return changeDate
      ? t(
          msg`This user will switch to annual billing at the end of the billing period (${changeDate})`
        )
      : t(
          msg`This user will switch to annual billing at the end of the billing period`
        );
  }

  const currentRank = currentSeatType ? SEAT_TYPE_ORDER[currentSeatType] : 0;
  const scheduledRank = SEAT_TYPE_ORDER[scheduledSeatType];
  const targetLabel = getSeatName(scheduledSeatType, t);
  if (scheduledRank > currentRank) {
    return changeDate
      ? t(
          msg`This user will be upgraded to ${targetLabel} at the end of the billing period (${changeDate})`
        )
      : t(
          msg`This user will be upgraded to ${targetLabel} at the end of the billing period`
        );
  }
  if (scheduledRank < currentRank) {
    return changeDate
      ? t(
          msg`This user will be downgraded to ${targetLabel} at the end of the billing period (${changeDate})`
        )
      : t(
          msg`This user will be downgraded to ${targetLabel} at the end of the billing period`
        );
  }
  return changeDate
    ? t(
        msg`This user will be changed to ${targetLabel} at the end of the billing period (${changeDate})`
      )
    : t(
        msg`This user will be changed to ${targetLabel} at the end of the billing period`
      );
}

interface AwuUsageBarProps {
  consumed: number;
  // Of `consumed`, the part drawn from the seat allowance vs. the workspace
  // pool (+ overage). Provided by the API so the bar doesn't re-derive the
  // split. `consumedFromAllowance + consumedFromPool === consumed`.
  consumedFromAllowance: number;
  consumedFromPool: number;
  memberUsageLimit: number | null;
  // Live remaining Metronome balance for the per-user credit (free seats only).
  // When provided for a free seat, the bar shows lifetime consumed/remaining
  // instead of period spend.
  seatBalanceAwu?: number | null;
  // The fully-resolved spend cap from `spendLimitAwuCredits` (member override,
  // group cap or workspace default, all including seat allowance). `null`
  // means no cap could be resolved and is treated as no pool access (capped
  // at the seat allowance) — unlimited/uncapped is not a supported product
  // state right now.
  effectiveLimit: number | null;
  // Where `effectiveLimit` comes from — shown as a tooltip on the limit figure.
  spendLimitSource: EffectiveSpendLimitSource;
  // Name of the group behind `effectiveLimit` when `spendLimitSource` is
  // `"group"`. Null for every other source.
  spendLimitGroupName: string | null;
  seatType: MembershipSeatType | null;
  isTotalAllowedUsagePending: boolean;
}

// Human-readable origin of the effective spend limit, or null when there is
// nothing worth explaining (no limit configured).
function spendLimitSourceLabel(
  source: EffectiveSpendLimitSource,
  groupName: string | null,
  t: Translate
): string | null {
  switch (source) {
    case "override":
      return t(msg`Limit set specifically for this member`);
    case "group":
      return groupName
        ? t(msg`Limit inherited from the "${groupName}" group`)
        : t(msg`Limit from a group`);
    case "default":
      return t(msg`Workspace default limit`);
    case "none":
      return null;
    default:
      assertNeverAndIgnore(source);
      return null;
  }
}

interface PoolCreditUsageBarProps {
  consumedFromPool: number;
  memberUsageLimit: number | null;
  // Resolved spend cap including the seat allowance; `null` means no pool
  // access, i.e. a zero pool limit.
  effectiveLimit: number | null;
  isTotalAllowedUsagePending: boolean;
  poolCapOverrideExpiresAt: string | null;
  poolCapOverridePreviousAwuCredits: number | null;
}

// Single-segment bar showing only the workspace pool share of a member's
// spend against their pool headroom (spend cap minus seat allowance).
function PoolCreditUsageBar({
  consumedFromPool,
  memberUsageLimit,
  effectiveLimit,
  isTotalAllowedUsagePending: isPending,
  poolCapOverrideExpiresAt,
  poolCapOverridePreviousAwuCredits,
}: PoolCreditUsageBarProps) {
  const { t } = useLingui();
  const poolLimit = computePoolLimitAwuCredits({
    memberUsageLimit,
    effectiveLimit,
  });
  const isOverPoolLimit = consumedFromPool > poolLimit;
  const isAtPoolLimit = poolLimit > 0 && consumedFromPool === poolLimit;
  const percentage =
    poolLimit > 0
      ? Math.min(100, (consumedFromPool / poolLimit) * 100)
      : consumedFromPool > 0
        ? 100
        : 0;
  const limitLabel = formatCredits(poolLimit);
  const resetDate =
    poolCapOverrideExpiresAt !== null
      ? formatConsumptionDate(poolCapOverrideExpiresAt, getActiveLocale())
      : null;
  const previousLimit =
    poolCapOverridePreviousAwuCredits !== null
      ? formatCredits(poolCapOverridePreviousAwuCredits)
      : null;
  const temporaryResetLabel =
    resetDate !== null
      ? previousLimit === null
        ? t`Resets to no personal limit on ${resetDate}`
        : t`Resets to ${previousLimit} credits on ${resetDate}`
      : null;
  const consumedLabel = formatCredits(consumedFromPool);
  return (
    <div className="flex w-full flex-col gap-1">
      <div className="flex justify-between text-xs tabular-nums text-foreground">
        <span>{consumedLabel}</span>
        {isPending ? (
          <Spinner size="xs" />
        ) : (
          <span className="inline-flex items-center gap-1">
            <span>{limitLabel}</span>
            {temporaryResetLabel && (
              <Tooltip
                tooltipTriggerAsChild
                label={temporaryResetLabel}
                trigger={
                  <span className="inline-flex text-muted-foreground">
                    <Icon visual={Clock} size="xs" />
                  </span>
                }
              />
            )}
          </span>
        )}
      </div>
      <div className="flex h-3 w-full items-center">
        <ProgressBar
          aria-label={t`Member pool credit usage`}
          aria-valuenow={percentage}
          aria-valuetext={t`${consumedLabel} of ${limitLabel} pool credits used`}
          className="h-1 w-full gap-px"
          variant="transparent"
          values={[
            {
              value: percentage,
              className: isOverPoolLimit
                ? OVER_POOL_LIMIT_BAR_CLASSES.fill
                : isAtPoolLimit
                  ? AT_POOL_LIMIT_BAR_CLASSES.fill
                  : MUTED_BAR_CLASSES.fill,
            },
            { value: 100 - percentage, className: MUTED_BAR_CLASSES.track },
          ]}
        />
      </div>
    </div>
  );
}

export function AwuUsageBar({
  consumed,
  consumedFromAllowance,
  consumedFromPool,
  memberUsageLimit,
  seatBalanceAwu,
  effectiveLimit,
  spendLimitSource,
  spendLimitGroupName,
  seatType,
  isTotalAllowedUsagePending: isPending,
}: AwuUsageBarProps) {
  const { t } = useLingui();
  const seatColors = getSeatBarClasses(seatType);
  const allowance = memberUsageLimit ?? 0;
  const sourceLabel = spendLimitSourceLabel(
    spendLimitSource,
    spendLimitGroupName,
    t
  );
  // For free seats: use lifetime consumed (derived from the live Metronome
  // balance) instead of period spend, so the bar reflects remaining credit.
  const { consumed: seatConsumed, isFreeWithBalance } = computeSeatUsage({
    seatType,
    memberUsageLimit,
    seatBalanceAwu: seatBalanceAwu ?? null,
    consumedFromAllowanceAwuCredits: consumedFromAllowance,
  });
  // Unlimited/uncapped is not a supported product state right now: a `null`
  // effective limit is treated as no pool access (capped at the seat
  // allowance), same as an explicit "none" seat.
  // The bar splits consumption into seat → pool → overage:
  //   seat consumed · seat remaining · pool consumed · pool remaining · overage
  // `poolLimit` is the headroom above the seat allowance (0 = seat-only, e.g. free).
  // A seat with no pool (poolLimit === 0) shows no pool section —
  // any spend beyond the seat allowance is overage. Zero-width sections are
  // skipped.
  const seatRemaining = isFreeWithBalance
    ? seatBalanceAwu!
    : Math.max(0, allowance - seatConsumed);
  const resolvedEffectiveLimit = effectiveLimit ?? allowance;
  const poolLimit = computePoolLimitAwuCredits({
    memberUsageLimit,
    effectiveLimit,
  });
  // Of the pool consumption, the part within the pool limit vs. the overage
  // beyond it.
  const poolConsumed = Math.min(consumedFromPool, poolLimit);
  const poolRemaining = Math.max(0, poolLimit - poolConsumed);
  const overage = Math.max(0, consumedFromPool - poolLimit);

  const sections: Array<{
    value: number;
    className: string;
    label: string;
  }> = [];
  const seatConsumedLabel = formatCredits(seatConsumed);
  const seatRemainingLabel = formatCredits(seatRemaining);
  const allowanceLabel = formatCredits(allowance);
  const poolConsumedLabel = formatCredits(poolConsumed);
  const poolRemainingLabel = formatCredits(poolRemaining);
  const poolLimitLabel = formatCredits(poolLimit);
  const overageLabel = formatCredits(overage);
  if (seatConsumed > 0) {
    sections.push({
      value: seatConsumed,
      className: seatColors.fill,
      label: isFreeWithBalance
        ? t`${seatConsumedLabel} of ${allowanceLabel} lifetime credits used`
        : t`${seatConsumedLabel} of ${allowanceLabel} seat allowance used`,
    });
  }
  if (seatRemaining > 0) {
    sections.push({
      value: seatRemaining,
      className: seatColors.track,
      label: isFreeWithBalance
        ? t`${seatRemainingLabel} of ${allowanceLabel} lifetime credits remaining`
        : t`${seatRemainingLabel} of ${allowanceLabel} seat allowance remaining`,
    });
  }
  if (poolConsumed > 0) {
    sections.push({
      value: poolConsumed,
      className: MUTED_BAR_CLASSES.fill,
      label: t`${poolConsumedLabel} credits used from the workspace pool`,
    });
  }
  if (poolRemaining > 0) {
    sections.push({
      value: poolRemaining,
      className: MUTED_BAR_CLASSES.track,
      label: t`${poolRemainingLabel} credits remaining before spend limit`,
    });
  }

  const total = sections.reduce((sum, s) => sum + s.value, 0);
  const usedPercentage =
    total > 0
      ? Math.min(
          100,
          Math.max(0, ((seatConsumed + poolConsumed) / total) * 100)
        )
      : 0;

  const hasSeatSections = seatConsumed > 0 || seatRemaining > 0;
  // Only surface the pool when there's actually a pool to spend from (a
  // zero pool limit, e.g. free/none, has no pool).
  const hasPoolSections =
    poolLimit > 0 && (poolConsumed > 0 || poolRemaining > 0);

  const tooltipLines: Array<{
    track: string;
    fill: string;
    legend: string;
    usage: string;
  }> = [];
  if (hasSeatSections) {
    tooltipLines.push({
      track: seatColors.track,
      fill: seatColors.fill,
      legend: isFreeWithBalance ? t`Lifetime credits` : t`Seat usage`,
      usage: t`${seatConsumedLabel} credits used out of ${allowanceLabel}`,
    });
  }
  if (hasPoolSections) {
    tooltipLines.push({
      track: MUTED_BAR_CLASSES.track,
      fill: MUTED_BAR_CLASSES.fill,
      legend: t`Pool usage`,
      usage: t`${poolConsumedLabel} credits used out of ${poolLimitLabel}`,
    });
  }
  if (overage > 0) {
    tooltipLines.push({
      track: OVERAGE_BAR_CLASSES.track,
      fill: OVERAGE_BAR_CLASSES.fill,
      legend: t`Overage`,
      usage: t`${overageLabel} credits over the spend limit`,
    });
  }

  const tooltipContent =
    tooltipLines.length > 0 ? (
      <div className="flex flex-col gap-1">
        {tooltipLines.map((line) => (
          <div key={line.legend} className="flex items-center gap-2">
            <div className="relative h-2.5 w-2.5 overflow-hidden rounded-sm">
              <div
                className={`absolute inset-0 ${line.track}`}
                style={{ clipPath: "polygon(0 0, 100% 0, 0 100%)" }}
              />
              <div
                className={`absolute inset-0 ${line.fill}`}
                style={{ clipPath: "polygon(100% 0, 100% 100%, 0 100%)" }}
              />
            </div>
            <span>
              {line.legend} — {line.usage}
            </span>
          </div>
        ))}
      </div>
    ) : null;

  const bar = (
    <div className="flex h-3 w-full items-center">
      <ProgressBar
        aria-label={t`Member credit usage`}
        aria-valuenow={usedPercentage}
        aria-valuetext={
          sections.length > 0
            ? sections.map((section) => section.label).join(", ")
            : t`No credits available`
        }
        className="h-1 w-full gap-px"
        variant="transparent"
        values={
          sections.length > 0
            ? sections.map(({ value, className }) => ({ value, className }))
            : [{ value: 1, className: MUTED_BAR_CLASSES.track }]
        }
      />
    </div>
  );

  const headlineConsumed = isFreeWithBalance
    ? Math.min(seatConsumed + overage, allowance)
    : Math.min(consumed, resolvedEffectiveLimit);
  const headlineLimit = isFreeWithBalance ? allowance : resolvedEffectiveLimit;
  const headlineLimitLabel = formatCredits(headlineLimit);
  return (
    <div className="flex w-full flex-col gap-1">
      <div className="flex justify-between text-xs tabular-nums text-foreground">
        <span>{formatCredits(headlineConsumed)}</span>
        {isPending ? (
          <Spinner size="xs" />
        ) : !isFreeWithBalance && sourceLabel !== null ? (
          <Tooltip
            tooltipTriggerAsChild
            label={sourceLabel}
            trigger={<span>{headlineLimitLabel}</span>}
          />
        ) : (
          <span>{headlineLimitLabel}</span>
        )}
      </div>
      {tooltipContent ? (
        <Tooltip tooltipTriggerAsChild label={tooltipContent} trigger={bar} />
      ) : (
        bar
      )}
    </div>
  );
}

// Name is intentionally the only column without a set width, so it absorbs
// every bit of leftover space. Secondary columns hide (via the container
// queries in their meta.className) before Name has to shrink, keeping long
// names/emails readable.
const nameColumn = buildMemberNameColumn<RowData>();

function buildGroupsColumn(t: Translate): ColumnDef<RowData, string> {
  return {
    id: "groups" as const,
    header: t(msg`Groups`),
    enableSorting: false,
    accessorFn: (row) => row.groups.join(", "),
    cell: (info: Info) => {
      const { groups } = info.row.original;
      return (
        <DataTable.CellContent>
          <span className="text-sm text-muted-foreground">
            {groups.length > 0 ? groups.join(", ") : "--"}
          </span>
        </DataTable.CellContent>
      );
    },
    meta: {
      // Least essential: only appears on very wide containers (e.g. the Poke
      // pool-usage page), never crowding out Name on the sidebar-constrained
      // customer admin page.
      className: "hidden @6xl:table-cell @6xl:w-48",
    },
  };
}

function buildSeatsIconColumn(t: Translate): ColumnDef<RowData, string> {
  return {
    id: "seatsIcon" as const,
    header: t(msg`Seats`),
    enableSorting: false,
    accessorFn: (row) => row.seatType ?? "",
    cell: (info: Info) => {
      if (info.row.original.isSeatChangePending) {
        return (
          <DataTable.CellContent className="justify-center">
            <Spinner size="xs" />
          </DataTable.CellContent>
        );
      }
      const { seatType, scheduledSeatType, scheduledSeatChangeAt } =
        info.row.original;
      if (!seatType) {
        return (
          <DataTable.CellContent className="justify-center">
            <span className="text-sm text-muted-foreground">--</span>
          </DataTable.CellContent>
        );
      }
      const seatName = getSeatName(seatType, t);
      const tooltipLabel = scheduledSeatType
        ? getScheduledSeatChangeLabel(
            seatType,
            scheduledSeatType,
            scheduledSeatChangeAt,
            t
          )
        : seatType === "none"
          ? seatName
          : t(msg`${seatName} seat`);
      return (
        <DataTable.CellContent className="justify-center">
          <span className="flex items-center gap-1">
            <Tooltip
              tooltipTriggerAsChild
              label={tooltipLabel}
              trigger={
                <Chip
                  size="mini"
                  color={seatTypeChipColor(seatType)}
                  label={seatName}
                  className="cursor-default"
                />
              }
            />
            {scheduledSeatType && (
              // Visible badge that a seat change is scheduled; hovering it explains
              // what and when.
              <Tooltip
                tooltipTriggerAsChild
                label={getScheduledSeatChangeLabel(
                  seatType,
                  scheduledSeatType,
                  scheduledSeatChangeAt,
                  t
                )}
                trigger={
                  <span className="cursor-default text-muted-foreground">
                    <Icon visual={Clock} size="xs" />
                  </span>
                }
              />
            )}
          </span>
        </DataTable.CellContent>
      );
    },
    meta: {
      className: "hidden @3xl:table-cell @3xl:w-24",
      headerAlign: "center",
    },
  };
}

function buildSeatUsageColumn(t: Translate): ColumnDef<RowData, string> {
  return {
    id: "seatUsage" as const,
    header: t(msg`Seat usage`),
    enableSorting: true,
    sortDescFirst: true,
    // Sorting is server-side; the accessor only exists so the header is sortable.
    accessorFn: (row) => row.memberUsageLimit?.toString() ?? "",
    cell: (info: Info) => {
      const {
        seatType,
        memberUsageLimit,
        seatBalanceAwu,
        isSeatChangePending,
      } = info.row.original;
      const { percent, consumed, allowance } = computeSeatUsage({
        seatType,
        memberUsageLimit,
        seatBalanceAwu,
        consumedFromAllowanceAwuCredits:
          info.row.original.consumedFromAllowanceAwuCredits,
      });
      if (isSeatChangePending || percent === null) {
        return (
          <DataTable.CellContent className="justify-center">
            <span className="text-sm text-muted-foreground">--</span>
          </DataTable.CellContent>
        );
      }
      const consumedLabel = formatCredits(consumed);
      const allowanceLabel = formatCredits(allowance);
      return (
        <DataTable.CellContent className="justify-center">
          <Tooltip
            tooltipTriggerAsChild
            label={t(msg`${consumedLabel} / ${allowanceLabel} credits used`)}
            trigger={
              <span className="text-xs font-medium text-muted-foreground">
                {Math.round(percent)}%
              </span>
            }
          />
        </DataTable.CellContent>
      );
    },
    meta: {
      className: "hidden @4xl:table-cell @4xl:w-32",
      headerAlign: "center",
    },
  };
}

function buildPoolCreditUsageColumn(
  hasPool: boolean
): ColumnDef<RowData, string> {
  return {
    id: "consumedFromPoolAwuCredits" as const,
    header: () => (
      <div className="flex min-w-0 flex-col">
        <span className="flex min-w-0 items-center gap-1">
          <Icon visual={CoinsStacked03} size="xs" />
          <span className="truncate">
            {hasPool ? <Trans>Pool usage</Trans> : <Trans>Credit usage</Trans>}
          </span>
        </span>
      </div>
    ),
    accessorFn: (row) => row.consumedFromPoolAwuCredits.toString(),
    cell: (info: Info) => (
      <div className="w-full pr-3">
        <PoolCreditUsageBar
          consumedFromPool={info.row.original.consumedFromPoolAwuCredits}
          memberUsageLimit={info.row.original.memberUsageLimit}
          effectiveLimit={info.row.original.spendLimitAwuCredits}
          isTotalAllowedUsagePending={
            info.row.original.isTotalAllowedUsagePending
          }
          poolCapOverrideExpiresAt={info.row.original.poolCapOverrideExpiresAt}
          poolCapOverridePreviousAwuCredits={
            info.row.original.poolCapOverridePreviousAwuCredits
          }
        />
      </div>
    ),
    enableSorting: true,
  };
}

function buildPremiumMessageUsageColumn(
  windowDays: number,
  t: Translate
): ColumnDef<RowData, string> {
  return {
    id: "premiumMessageUsage" as const,
    header: () => (
      <div className="flex flex-col">
        <span className="flex items-center gap-1">
          <Icon visual={CoinsStacked03} size="xs" />
          <Trans>Premium messages</Trans>
        </span>
        <span className="text-xs font-normal text-muted-foreground">
          <Trans>Resets on a rolling {windowDays}-day basis</Trans>
        </span>
      </div>
    ),
    accessorFn: (row) =>
      (row.premiumMessageUsage?.usedMessages ?? 0).toString(),
    cell: (info: Info) => {
      const usage = info.row.original.premiumMessageUsage;
      if (!usage) {
        return (
          <DataTable.CellContent className="justify-center">
            <span className="text-sm text-muted-foreground">--</span>
          </DataTable.CellContent>
        );
      }
      const { usedMessages, limitMessages, windowDays, refillSchedule } = usage;
      const percentage =
        limitMessages > 0
          ? Math.min(100, (usedMessages / limitMessages) * 100)
          : usedMessages > 0
            ? 100
            : 0;
      const isAtLimit = limitMessages > 0 && usedMessages === limitMessages;
      const bar = (
        <ProgressBar
          aria-label={t(msg`Premium message usage`)}
          aria-valuenow={percentage}
          aria-valuetext={t(
            msg`${usedMessages} of ${limitMessages} premium messages used over the last ${windowDays} days`
          )}
          className="h-1 w-full gap-px"
          variant="transparent"
          values={[
            {
              value: percentage,
              className: isAtLimit
                ? AT_POOL_LIMIT_BAR_CLASSES.fill
                : MUTED_BAR_CLASSES.fill,
            },
            { value: 100 - percentage, className: MUTED_BAR_CLASSES.track },
          ]}
        />
      );
      return (
        <div className="flex w-full flex-col gap-1 pr-3">
          <div className="flex justify-between text-xs tabular-nums text-foreground">
            <span>{usedMessages}</span>
            <span>{limitMessages}</span>
          </div>
          {refillSchedule && refillSchedule.length > 0 ? (
            <Tooltip
              tooltipTriggerAsChild
              trigger={
                <div className="flex h-3 w-full cursor-help items-center">
                  {bar}
                </div>
              }
              label={
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium">
                    <Trans>Reset schedule:</Trans>
                  </span>
                  {refillSchedule.map(({ date, messages }) => (
                    <span key={date}>
                      {formatDate(
                        new Date(date),
                        {
                          month: "short",
                          day: "numeric",
                          timeZone: "UTC",
                        },
                        getActiveLocale()
                      )}
                      : +{messages}
                    </span>
                  ))}
                </div>
              }
            />
          ) : (
            <div className="flex h-3 w-full items-center">{bar}</div>
          )}
        </div>
      );
    },
    enableSorting: true,
  };
}

function buildFairUseCreditsColumn(
  windowDays: number,
  t: Translate
): ColumnDef<RowData, string> {
  return {
    id: "fairUse" as const,
    header: () => (
      <div className="flex flex-col">
        <span className="flex items-center gap-1">
          <Icon visual={CoinsStacked03} size="xs" />
          <Trans>Fair Usage Credits</Trans>
        </span>
        <span className="text-xs font-normal text-muted-foreground">
          <Trans>Resets on a rolling {windowDays}-day basis</Trans>
        </span>
      </div>
    ),
    accessorFn: (row) => (row.fairUse?.usedCredits ?? 0).toString(),
    cell: (info: Info) => {
      const fairUse = info.row.original.fairUse;
      if (!fairUse) {
        return (
          <DataTable.CellContent className="justify-center">
            <span className="text-sm text-muted-foreground">--</span>
          </DataTable.CellContent>
        );
      }
      const { usedCredits, limitCredits, refillSchedule } = fairUse;
      const percentage =
        limitCredits > 0
          ? Math.min(100, (usedCredits / limitCredits) * 100)
          : usedCredits > 0
            ? 100
            : 0;
      const isAtLimit = limitCredits > 0 && usedCredits >= limitCredits;
      const usedLabel = formatCredits(usedCredits);
      const limitLabel = formatCredits(limitCredits);
      const limitCreditCount = roundCredits(limitCredits);
      const bar = (
        <ProgressBar
          aria-label={t(msg`Fair-use credits usage`)}
          aria-valuenow={percentage}
          aria-valuetext={t(
            msg`${plural(limitCreditCount, {
              one: `${usedLabel} of ${limitLabel} credit used`,
              other: `${usedLabel} of ${limitLabel} credits used`,
            })}`
          )}
          className="w-full"
          variant="transparent"
          values={[
            {
              value: percentage,
              className: isAtLimit
                ? AT_POOL_LIMIT_BAR_CLASSES.fill
                : MUTED_BAR_CLASSES.fill,
            },
            { value: 100 - percentage, className: MUTED_BAR_CLASSES.track },
          ]}
        />
      );
      return (
        <div className="flex w-full flex-col gap-1">
          <div className="flex justify-between text-xs tabular-nums text-foreground">
            <span>{formatCredits(usedCredits)}</span>
            <span>{formatCredits(limitCredits)}</span>
          </div>
          {refillSchedule.length > 0 ? (
            <Tooltip
              tooltipTriggerAsChild
              trigger={
                <div className="flex h-3 w-full cursor-help items-center">
                  {bar}
                </div>
              }
              label={
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium">
                    <Trans>Reset schedule:</Trans>
                  </span>
                  {refillSchedule.map(({ date, credits }) => (
                    <span key={date}>
                      {formatDate(
                        new Date(date),
                        {
                          month: "short",
                          day: "numeric",
                          timeZone: "UTC",
                        },
                        getActiveLocale()
                      )}
                      : +{formatCredits(credits)}
                    </span>
                  ))}
                </div>
              }
            />
          ) : (
            <div className="flex h-3 w-full items-center">{bar}</div>
          )}
        </div>
      );
    },
    enableSorting: true,
    sortDescFirst: true,
    meta: {
      className: "w-56",
    },
  };
}

function buildOffPaceColumn(t: Translate): ColumnDef<RowData, string> {
  return {
    id: "overallUsageTarget" as const,
    header: "",
    enableSorting: false,
    accessorFn: (row) => row.overallUsageTarget ?? "",
    cell: (info: Info) => {
      const {
        overallUsageTarget,
        isSpendCapped,
        canUpgradeSeat,
        seatType,
        onOpenChangeSeatRecap,
        onOpenSpendLimitRecap,
      } = info.row.original;

      if (isSpendCapped) {
        // Free seats have no pool credits to raise (their cap is just the
        // seat's built-in allowance), so seat upgrade is the only unblock path.
        const isFreeSeat = seatType === "free";

        if (isFreeSeat) {
          return (
            <DataTable.CellContent className="justify-center">
              <div
                onClick={(event) => event.stopPropagation()}
                onPointerDown={(event) => event.stopPropagation()}
              >
                <Button
                  variant="highlight"
                  size="xs"
                  label={t(msg`Unblock`)}
                  disabled={!canUpgradeSeat}
                  onClick={onOpenChangeSeatRecap}
                />
              </div>
            </DataTable.CellContent>
          );
        }

        if (!canUpgradeSeat) {
          return (
            <DataTable.CellContent className="justify-center">
              <div
                onClick={(event) => event.stopPropagation()}
                onPointerDown={(event) => event.stopPropagation()}
              >
                <Button
                  variant="highlight"
                  size="xs"
                  label={t(msg`Unblock`)}
                  onClick={onOpenSpendLimitRecap}
                />
              </div>
            </DataTable.CellContent>
          );
        }
        return (
          <DataTable.CellContent className="justify-center">
            <div
              onClick={(event) => event.stopPropagation()}
              onPointerDown={(event) => event.stopPropagation()}
            >
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="highlight"
                    size="xs"
                    label={t(msg`Unblock`)}
                    isSelect
                  />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    label={t(msg`Upgrade seat`)}
                    onClick={onOpenChangeSeatRecap}
                  />
                  <DropdownMenuItem
                    label={t(msg`Edit spend limit`)}
                    onClick={onOpenSpendLimitRecap}
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </DataTable.CellContent>
        );
      }

      if (
        overallUsageTarget !== "elevated" &&
        overallUsageTarget !== "critical"
      ) {
        return <DataTable.CellContent className="justify-center" />;
      }
      const isCritical = overallUsageTarget === "critical";
      return (
        <DataTable.CellContent className="justify-center">
          <Tooltip
            tooltipTriggerAsChild
            label={
              isCritical
                ? t(
                    msg`At this rate, this user will reach their limit before the cycle ends and lose access to Azimut until it resets.`
                  )
                : t(msg`Consuming credits ahead of the billing cycle's pace`)
            }
            trigger={
              <span className="flex cursor-default items-center justify-center">
                <Icon
                  visual={AlertCircle}
                  size="sm"
                  className={isCritical ? "text-red-500" : "text-warning-500"}
                />
              </span>
            }
          />
        </DataTable.CellContent>
      );
    },
    meta: {
      className: "hidden @4xl:table-cell @4xl:w-28",
      headerAlign: "center",
    },
  };
}

function buildModelTiersColumn(t: Translate): ColumnDef<RowData, string> {
  return {
    id: "modelTiers" as const,
    header: () => (
      <span className="flex items-center gap-1">
        <Trans>Models</Trans>
        <ModelTiersInfoButton />
      </span>
    ),
    enableSorting: false,
    accessorFn: (row) => row.modelTiersSummary,
    cell: (info: Info) => {
      const summary = info.row.original.modelTiersSummary;

      return (
        <DataTable.CellContent>
          <span className="text-sm text-muted-foreground dark:text-muted-foreground-night">
            {info.row.original.hasUserLevelModelTiersOverride
              ? t(msg`${summary} (custom)`)
              : summary}
          </span>
        </DataTable.CellContent>
      );
    },
    meta: {
      // The extra seat + off-pace columns compete for width, so Models tier
      // yields sooner to keep Name readable.
      className: "hidden @6xl:table-cell @6xl:w-48",
    },
  };
}

const actionsColumn: ColumnDef<RowData, string> = {
  id: "actions" as const,
  header: "",
  enableSorting: false,
  accessorKey: "actions",
  cell: (info: Info) => (
    <div
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <DataTable.MoreButton menuItems={info.row.original.menuItems} />
    </div>
  ),
  meta: {
    className: "w-14",
  },
};

function buildCreditPlanColumns({
  hasPool,
  showPremiumMessageUsage,
  premiumMessageWindowDays,
  fairUseWindowDays,
  showUnblockWidth,
  t,
}: {
  hasPool: boolean;
  showPremiumMessageUsage: boolean;
  premiumMessageWindowDays: number;
  fairUseWindowDays: number | null;
  showUnblockWidth: boolean;
  t: Translate;
}): ColumnDef<RowData, string>[] {
  const offPaceColumn = buildOffPaceColumn(t);
  return [
    // Premium message plans have no seats: every member is billed per
    // message, so the seat columns have nothing to show.
    ...(showPremiumMessageUsage
      ? []
      : [buildSeatsIconColumn(t), buildSeatUsageColumn(t)]),
    {
      ...(showPremiumMessageUsage
        ? buildPremiumMessageUsageColumn(premiumMessageWindowDays, t)
        : buildPoolCreditUsageColumn(hasPool)),
      meta: { className: "w-32 @xs:w-56" },
    },
    // Premium message plans also carry a fixed AWU credit allowance for
    // usage on non-premium models, alongside the rolling message limit.
    ...(showPremiumMessageUsage && fairUseWindowDays !== null
      ? [buildFairUseCreditsColumn(fairUseWindowDays, t)]
      : []),
    // Icon-wide until an "Unblock" button has shown up, button-wide from then on.
    ...(!showPremiumMessageUsage
      ? [
          showUnblockWidth
            ? offPaceColumn
            : {
                ...offPaceColumn,
                meta: {
                  ...offPaceColumn.meta,
                  className: "hidden @4xl:table-cell @4xl:w-12",
                },
              },
        ]
      : []),
  ];
}

function buildColumns({
  enableSelection,
  showGroupsColumn,
  showModelTiersColumn,
  showSeatAndCredits,
  hasPool,
  showPremiumMessageUsage,
  premiumMessageWindowDays,
  fairUseWindowDays,
  showUnblockWidth,
  t,
}: {
  enableSelection: boolean;
  showGroupsColumn: boolean;
  showModelTiersColumn: boolean;
  showSeatAndCredits: boolean;
  hasPool: boolean;
  showPremiumMessageUsage: boolean;
  premiumMessageWindowDays: number;
  fairUseWindowDays: number | null;
  showUnblockWidth: boolean;
  t: Translate;
}): ColumnDef<RowData, string>[] {
  return [
    ...(enableSelection ? [createSelectionColumn<RowData>()] : []),
    nameColumn,
    ...(showGroupsColumn ? [buildGroupsColumn(t)] : []),
    ...(showModelTiersColumn ? [buildModelTiersColumn(t)] : []),
    ...(showSeatAndCredits
      ? buildCreditPlanColumns({
          hasPool,
          showPremiumMessageUsage,
          premiumMessageWindowDays,
          fairUseWindowDays,
          showUnblockWidth,
          t,
        })
      : []),
    // Every row action belongs to one of these two groups.
    ...(showSeatAndCredits || showModelTiersColumn ? [actionsColumn] : []),
  ];
}

interface MembersUsageTableProps {
  members: MemberUsageType[];
  isLoading: boolean;
  isRefreshing?: boolean;
  totalAllowedUsagePendingMemberIds: ReadonlySet<string>;
  seatChangePendingMemberIds: ReadonlySet<string>;
  isSeatBased: boolean;
  showSpendLimit: boolean;
  canEditSpendLimit?: (member: MemberUsageType) => boolean;
  // Disables every row action
  readOnly?: boolean;
  // Seat and credits usage columns plus the seat row actions. Off for
  // workspaces that are not on a credit plan.
  showSeatAndCredits?: boolean;
  // Keep credit usage visible without offering seat changes to group managers.
  showSeatActions?: boolean;
  // Disables only the seat-assign/change/remove actions (e.g. while the
  // subscription has a cancellation scheduled), independent of `readOnly`.
  seatActionsDisabled?: boolean;
  onChangeSeat: (member: MemberUsageType) => void;
  onRemoveSeat: (member: MemberUsageType) => void;
  onEditSpendLimit: (member: MemberUsageType) => void;
  onOpenChangeSeatRecap?: (member: MemberUsageType) => void;
  onOpenSpendLimitRecap?: (member: MemberUsageType) => void;
  canUpgradeSeat?: (member: MemberUsageType) => boolean;
  onSetUserModelTier?: (
    member: MemberUsageType,
    selection: UserModelTierSelection
  ) => void;
  showModelTiersColumn?: boolean;
  // Whether the workspace has an active credit pool. Only affects the credit
  // column header.
  hasPool?: boolean;
  showPremiumMessageUsage?: boolean;
  userModelTierSelectionByUserId?: Record<string, UserModelTierSelection>;
  userAllowedModelTiersByUserId?: Record<string, ModelsTierName[]>;
  groupModelTiersByGroupId?: Record<string, ModelsTierName[]>;
  workspaceAllowedModelTiers?: ModelsTierName[];
  groupNameToId?: Map<string, string>;
  modelTierDefinitionByName?: Map<ModelsTierName, ModelsTierDefinition>;
  pagination: PaginationState;
  setPagination: (pagination: PaginationState) => void;
  totalRowCount: number;
  sorting: SortingState;
  setSorting: (sorting: SortingState) => void;
  showGroupsColumn?: boolean;
  enableSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (selection: RowSelectionState) => void;
}

export function MembersUsageTable({
  members,
  isLoading,
  isRefreshing = false,
  totalAllowedUsagePendingMemberIds,
  seatChangePendingMemberIds,
  isSeatBased,
  showSpendLimit,
  canEditSpendLimit = ALWAYS_CAN_EDIT_SPEND_LIMIT,
  readOnly = false,
  showSeatAndCredits = true,
  showSeatActions = true,
  seatActionsDisabled = false,
  onChangeSeat,
  onRemoveSeat,
  onEditSpendLimit,
  onOpenChangeSeatRecap = NOOP_ON_MEMBER,
  onOpenSpendLimitRecap = NOOP_ON_MEMBER,
  canUpgradeSeat = ALWAYS_CAN_UPGRADE_SEAT,
  onSetUserModelTier,
  showModelTiersColumn = false,
  hasPool = true,
  showPremiumMessageUsage = false,
  userModelTierSelectionByUserId = EMPTY_USER_MODEL_TIER_SELECTION_BY_USER_ID,
  userAllowedModelTiersByUserId = EMPTY_USER_ALLOWED_MODEL_TIERS_BY_USER_ID,
  groupModelTiersByGroupId = EMPTY_GROUP_MODEL_TIERS_BY_GROUP_ID,
  workspaceAllowedModelTiers = EMPTY_WORKSPACE_ALLOWED_MODEL_TIERS,
  groupNameToId = EMPTY_GROUP_NAME_TO_ID,
  modelTierDefinitionByName = EMPTY_MODEL_TIER_DEFINITION_BY_NAME,
  pagination,
  setPagination,
  totalRowCount,
  sorting,
  setSorting,
  showGroupsColumn = false,
  enableSelection = false,
  rowSelection,
  onRowSelectionChange,
}: MembersUsageTableProps) {
  const { t } = useLingui();
  const rows: RowData[] = useMemo(
    () =>
      members.map((m) => {
        const hasSeat = m.seatType !== null && m.seatType !== "none";
        const canEditSeat =
          showSeatActions && showSeatAndCredits && isSeatBased && hasSeat;
        const resolvedModelTiers = showModelTiersColumn
          ? resolveModelTiersForUser({
              userId: m.sId,
              groupNames: m.groups,
              groupNameToId,
              userAllowedTierNamesByUserId: userAllowedModelTiersByUserId,
              groupTierNamesByGroupId: groupModelTiersByGroupId,
              workspaceAllowedTierNames: workspaceAllowedModelTiers,
            })
          : null;

        return {
          sId: m.sId,
          name: m.name,
          email: m.email,
          image: m.image,
          groups: m.groups,
          seatType: m.seatType,
          memberUsageLimit: m.memberUsageLimit,
          seatBalanceAwu: m.seatBalanceAwu,
          consumedAwuCredits: m.consumedAwuCredits,
          consumedFromAllowanceAwuCredits: m.consumedFromAllowanceAwuCredits,
          consumedFromPoolAwuCredits: m.consumedFromPoolAwuCredits,
          spendLimitAwuCredits: m.spendLimitAwuCredits,
          spendLimitSource: m.spendLimitSource,
          spendLimitGroupName: m.spendLimitGroupName,
          poolCapOverrideExpiresAt: m.poolCapOverrideExpiresAt,
          poolCapOverridePreviousAwuCredits:
            m.poolCapOverridePreviousAwuCredits,
          scheduledSeatType: m.scheduledSeatType,
          scheduledSeatChangeAt: m.scheduledSeatChangeAt,
          isTotalAllowedUsagePending: totalAllowedUsagePendingMemberIds.has(
            m.sId
          ),
          isSeatChangePending: seatChangePendingMemberIds.has(m.sId),
          overallUsageTarget: m.overallUsageTarget,
          isSpendCapped: m.isSpendCapped,
          canUpgradeSeat: showSeatActions && canUpgradeSeat(m),
          onOpenChangeSeatRecap: () => onOpenChangeSeatRecap(m),
          onOpenSpendLimitRecap: () => onOpenSpendLimitRecap(m),
          premiumMessageUsage: m.premiumMessageUsage ?? null,
          fairUse: m.fairUse ?? null,
          modelTiersSummary: (() => {
            const maxTierName = getMaxTierName(resolvedModelTiers?.tiers ?? []);
            return maxTierName
              ? t(MODELS_TIER_DISPLAY_NAMES[maxTierName])
              : "--";
          })(),
          hasUserLevelModelTiersOverride: resolvedModelTiers?.source === "user",
          menuItems: [
            ...(showSeatActions &&
            showSeatAndCredits &&
            !hasSeat &&
            !showPremiumMessageUsage
              ? [
                  {
                    kind: "item" as const,
                    label: t`Assign seat`,
                    disabled: readOnly || seatActionsDisabled,
                    onClick: () => onChangeSeat(m),
                  },
                ]
              : []),
            ...(canEditSeat
              ? [
                  {
                    kind: "item" as const,
                    label: t`Change seat type`,
                    disabled: readOnly || seatActionsDisabled,
                    onClick: () => onChangeSeat(m),
                  },
                ]
              : []),
            ...(showSpendLimit &&
            isMembershipSeatType(m.seatType) &&
            isPaidSeatType(m.seatType)
              ? [
                  {
                    kind: "item" as const,
                    label: t`Edit spend limit`,
                    disabled: readOnly || !canEditSpendLimit(m),
                    onClick: () => onEditSpendLimit(m),
                  },
                ]
              : []),
            ...(showModelTiersColumn && onSetUserModelTier
              ? [
                  {
                    kind: "submenu" as const,
                    label: t`Models tier`,
                    selectionMode: "checkbox" as const,
                    items: getUserModelTierMenuItemsWithSelection({
                      selectedValue:
                        userModelTierSelectionByUserId[m.sId] ??
                        INHERIT_MODEL_TIER,
                      inheritLabel: formatUserModelTierInheritLabel({
                        groupNames: m.groups,
                        groupNameToId,
                        groupTierNamesByGroupId: groupModelTiersByGroupId,
                        workspaceAllowedTierNames: workspaceAllowedModelTiers,
                        t,
                      }),
                      t,
                    }).map((tierItem) => ({
                      id: tierItem.id,
                      name: tierItem.name,
                      description: tierItem.description,
                      checked: tierItem.checked,
                    })),
                    onSelect: (itemId: string) =>
                      onSetUserModelTier(m, toUserModelTierSelection(itemId)),
                  },
                ]
              : []),
            ...(canEditSeat
              ? [
                  {
                    kind: "item" as const,
                    label: t`Remove seat`,
                    variant: "warning" as const,
                    disabled: readOnly || seatActionsDisabled,
                    onClick: () => onRemoveSeat(m),
                  },
                ]
              : []),
          ],
        };
      }),
    [
      members,
      totalAllowedUsagePendingMemberIds,
      seatChangePendingMemberIds,
      isSeatBased,
      showSpendLimit,
      canEditSpendLimit,
      showModelTiersColumn,
      userModelTierSelectionByUserId,
      userAllowedModelTiersByUserId,
      groupModelTiersByGroupId,
      workspaceAllowedModelTiers,
      groupNameToId,
      readOnly,
      showSeatAndCredits,
      showSeatActions,
      showPremiumMessageUsage,
      seatActionsDisabled,
      onChangeSeat,
      onRemoveSeat,
      onEditSpendLimit,
      onOpenChangeSeatRecap,
      onOpenSpendLimitRecap,
      canUpgradeSeat,
      onSetUserModelTier,
      t,
    ]
  );

  // All members share the same rolling-window/plan configuration, so the
  // first loaded usage payload is representative of the whole table.
  const premiumMessageWindowDays =
    members.find((m) => m.premiumMessageUsage)?.premiumMessageUsage
      ?.windowDays ?? DEFAULT_PREMIUM_MESSAGE_WINDOW_DAYS;
  const fairUseWindowDays =
    members.find((m) => m.fairUse)?.fairUse?.windowDays ?? null;

  // Whether a member is blocked is only known page by page, so the off-pace
  // column latches onto the button width the first time one shows up and
  // keeps it: widening once beats resizing on every page change.
  const hasSeenSpendCappedRowRef = useRef(false);
  if (members.some((m) => m.isSpendCapped)) {
    hasSeenSpendCappedRowRef.current = true;
  }
  const showUnblockWidth = hasSeenSpendCappedRowRef.current;

  const columns = useMemo(
    () =>
      buildColumns({
        enableSelection,
        showGroupsColumn,
        showModelTiersColumn,
        showSeatAndCredits,
        hasPool,
        showPremiumMessageUsage,
        premiumMessageWindowDays,
        fairUseWindowDays,
        showUnblockWidth,
        t,
      }),
    [
      enableSelection,
      showGroupsColumn,
      showModelTiersColumn,
      showSeatAndCredits,
      hasPool,
      premiumMessageWindowDays,
      showPremiumMessageUsage,
      fairUseWindowDays,
      showUnblockWidth,
      t,
    ]
  );

  if (isLoading) {
    const remainingRows =
      totalRowCount - pagination.pageIndex * pagination.pageSize;
    return (
      <div className="flex flex-col gap-2">
        <DataTableSkeleton
          columns={columns}
          SkeletonCell={MemberUsageSkeletonCell}
          rowCount={
            remainingRows > 0
              ? Math.min(pagination.pageSize, remainingRows)
              : pagination.pageSize
          }
        />
        <div className="flex h-8 items-center justify-end px-1">
          <LoadingBlock className="h-3 w-14" />
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div
        className={
          isRefreshing
            ? "pointer-events-none opacity-50 transition-opacity"
            : "transition-opacity"
        }
      >
        <DataTable
          data={rows}
          columns={columns}
          pagination={pagination}
          setPagination={setPagination}
          totalRowCount={totalRowCount}
          sorting={sorting}
          setSorting={setSorting}
          isServerSideSorting
          enableRowSelection={enableSelection}
          rowSelection={rowSelection}
          setRowSelection={onRowSelectionChange}
          getRowId={(row) => row.sId}
        />
      </div>
      {isRefreshing && (
        <div className="absolute inset-x-0 top-16 flex justify-center">
          <Spinner size="sm" />
        </div>
      )}
    </div>
  );
}
